import React, {useEffect, useState} from 'react';
import './SubmissionDetails.css';
import {Button, ButtonGroup} from '@mui/material';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faDownload,
    faUser,
    faAddressCard
} from "@awesome.me/kit-e6334c76d4/icons/sharp/solid";
import {apiFetch} from "../services/api";
import {Submission} from "./SubmissionsList";

interface FbiResponse {
    atn: string;
    ucn: string;
    name: string;
    pob: string;
    srf: string;
    repo: string;
    cri: string;
    notes: string;
}

interface StateResponse {
    sid: string;
    name: string;
    dob: string;
    srf: string;
    'case-type': string;
    'offender-type': string;
}

export interface SubmissionDetail {
    txId: string;
    state: string;
    fbiResponse?: FbiResponse;
    stateResponse?: StateResponse;
    faceImg?: string;
}

interface SubmissionDetailsProps {
    details: SubmissionDetail;
    submission: Submission;
}

const DetailSection: React.FC<{ title: string; data?: object }> = ({ title, data }) => {
    if (!data) return null;
    return (
        <div className="detail-section">
            <h4>{title}</h4>
            <div className="detail-grid">
                {Object.entries(data)
                    .filter(([key]) => key !== 'notes')
                    .map(([key, value]) => (
                        <div className="detail-item" key={key}>
                            <span className="detail-key">{key.toUpperCase()}:</span>
                            <span className="detail-value">{value}</span>
                        </div>
                    ))}
            </div>
        </div>
    );
};

const SubmissionDetails: React.FC<SubmissionDetailsProps> = ({ details, submission }) => {
    const [showResponseFace, setShowResponseFace] = useState(false);

    const submissionFaceUrl = submission.face ? `${submission.face}` : null;
    const responseFaceUrl = details.faceImg ? `data:image/png;base64,${details.faceImg}` : null;

    useEffect(() => {
        if (responseFaceUrl) {
            setShowResponseFace(true);
        }
    }, [responseFaceUrl]);

    const faceImageUrl = showResponseFace ? responseFaceUrl : submissionFaceUrl || responseFaceUrl || '/empty_face.png';

    const handleDownload = async (url: string, filename: string) => {
        try {
            const response = await apiFetch(url);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Failed to fetch submissions' }));
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            if (data.ebts) {
                const byteCharacters = atob(data.ebts);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/octet-stream' });
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (error) {
            console.error('Download failed:', error);
        }
    };

    return (
        <div className="submission-details-container">
            <div className="face-image-container">
                {submissionFaceUrl && responseFaceUrl && (
                    <ButtonGroup variant="outlined" size="small" className="face-toggle">
                        <Button onClick={() => setShowResponseFace(false)} disabled={!showResponseFace} title="Submission Face"><FontAwesomeIcon icon={faUser} size="lg" /></Button>
                        <Button onClick={() => setShowResponseFace(true)} disabled={showResponseFace} title="Response Face"><FontAwesomeIcon icon={faAddressCard} size="lg" /></Button>
                    </ButtonGroup>
                )}
                <img src={faceImageUrl} alt="Face" className="face-image" />
                <div className="download-buttons">
                    <Button variant="outline-primary" size="sm" onClick={() => handleDownload(`/api/risc/sub/${details.txId}/download`, `${details.txId}_sub.ebts`)}><FontAwesomeIcon icon={faDownload} /> Submission</Button>
                    {details.state === 'RESPONSE_RECEIVED' && (
                        <Button variant="outline-secondary" size="sm" onClick={() => handleDownload(`/api/risc/sub/${details.txId}/download/response`, `${details.txId}_resp.ebts`)}><FontAwesomeIcon icon={faDownload} /> Response</Button>
                    )}
                </div>
            </div>
            <div className="details-content">
                <DetailSection title="FBI Response" data={details.fbiResponse} />
                <DetailSection title="State Response" data={details.stateResponse} />
                {details.fbiResponse?.notes && (
                    <div className="detail-section">
                        <h4>Notes</h4>
                        <p className="notes-text">{details.fbiResponse.notes}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubmissionDetails;
